import React, { useCallback, useEffect, useRef, useState } from 'react';
import BeefreeEmailEditor from './BeefreeEmailEditor';
import {
  ICONS,
  SOCIAL,
  buildEmailHtml,
  hasUnsubscribe,
  iconUrl,
  loadHtmlIntoEditor,
  registerEmailBlocks,
  registerEmailComponents,
  registerRteActions,
  starterTemplate
} from './emailEditorSetup';

const csrfToken = () => document.querySelector('[name="csrf-token"]')?.content;
const jsonHeaders = () => ({ 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() });

const EDITOR_HEIGHT = 'calc(100vh - 230px)';

export const EmailPreviewFrame = ({ html, device = 'desktop', height = 640 }) => (
  <div className='flex justify-center bg-slate-100 rounded-lg p-4 overflow-auto'>
    <iframe
      title='Email preview'
      // Без allow-scripts: HTML письма не должен исполнять код в интерфейсе сервиса.
      sandbox='allow-popups allow-popups-to-escape-sandbox'
      srcDoc={html || '<p style="font-family:Arial;color:#6b7280;padding:24px">Empty email</p>'}
      className='bg-white shadow-sm rounded border border-slate-200 transition-all'
      style={{ width: device === 'mobile' ? 375 : '100%', maxWidth: device === 'mobile' ? 375 : 900, height }}
    />
  </div>
);

const Tab = ({ active, onClick, children }) => (
  <button
    type='button'
    onClick={onClick}
    className={`px-3 py-1.5 text-sm rounded-md ${active ? 'bg-white shadow-sm text-slate-900 font-medium' : 'text-slate-600 hover:text-slate-900'}`}
  >
    {children}
  </button>
);

export const TestSend = ({ templateId, senders, dirty, requireSaved = false, disabled = false, beforeSend }) => {
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState('');
  const [senderId, setSenderId] = useState(senders[0]?.value || '');
  const [state, setState] = useState(null);
  const sendingRef = useRef(false);

  if (!templateId && !beforeSend) return null;

  const send = async () => {
    if (sendingRef.current || disabled || (requireSaved && dirty && !beforeSend)) return;
    sendingRef.current = true;
    setState({ busy: true });
    try {
      const savedTemplateId = beforeSend ? await beforeSend() : templateId;
      if (!savedTemplateId) throw new Error('The email could not be saved. No test email was sent.');
      const res = await fetch(`/email_templates/${savedTemplateId}/test_send`, {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify({ to, email_sender_id: senderId })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Send failed');
      setState({ ok: `Test email sent to ${to}` });
    } catch (e) {
      setState({ error: e.message });
    } finally {
      sendingRef.current = false;
    }
  };

  return (
    <div className='relative'>
      <button type='button' onClick={() => setOpen(!open)} className='px-3 py-2 text-sm text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50'>
        Send test
      </button>
      {open && (
        <div className='absolute right-0 mt-2 w-80 bg-white border border-slate-200 rounded-lg shadow-lg p-4 z-50 space-y-3'>
          {beforeSend ? <p className='text-xs text-slate-600'>The current email will be saved before the test is sent.</p> : dirty && <p className='text-xs text-amber-700'>Unsaved changes are not included — save first.</p>}
          {senders.length === 0 ? (
            <p className='text-sm text-slate-600'>No active email sender on a verified domain yet.</p>
          ) : (
            <>
              <select className='border border-slate-300 rounded px-2 py-1.5 text-sm w-full' value={senderId} onChange={(e) => setSenderId(e.target.value)}>
                {senders.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
              <input className='border border-slate-300 rounded px-2 py-1.5 text-sm w-full' placeholder='you@company.com' value={to} onChange={(e) => setTo(e.target.value)} />
              <button type='button' disabled={state?.busy || !to || disabled || (requireSaved && dirty && !beforeSend)} onClick={send} className='w-full px-3 py-2 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50'>
                {state?.busy ? 'Sending…' : 'Send test email'}
              </button>
            </>
          )}
          {state?.ok && <p className='text-xs text-green-700'>{state.ok}</p>}
          {state?.error && <p className='text-xs text-red-600'>{state.error}</p>}
        </div>
      )}
    </div>
  );
};

const GrapesEmailEditor = ({ template, senders = [], publicBaseUrl, onPersisted }) => {
  const containerRef = useRef(null);
  const editorRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [id, setId] = useState(template?.id || null);
  const [name, setName] = useState(template?.name || '');
  const [subject, setSubject] = useState(template?.subject || '');
  const [preheader, setPreheader] = useState(template?.preheader || '');
  const [mode, setMode] = useState('visual');
  const [device, setDevice] = useState('desktop');
  const [htmlCode, setHtmlCode] = useState(template?.html || '');
  const [htmlEdited, setHtmlEdited] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // --- GrapesJS: грузится отдельным чанком, чтобы не раздувать общий бандл ---------
  useEffect(() => {
    let editor;
    let cancelled = false;

    Promise.all([
      import('grapesjs'),
      import('grapesjs-preset-newsletter'),
      fetch('/email_assets.json').then((r) => (r.ok ? r.json() : { initialAssets: [] }))
    ])
      .then(([grapes, preset, library]) => {
        if (cancelled) return;
        const iconAssets = [...SOCIAL, ...ICONS.map((i) => `icon${i}`)].map((n) => ({ src: iconUrl(publicBaseUrl, n), name: n, category: 'Icons' }));
        const libraryAssets = (library.initialAssets || []).map((a) => ({ src: a.url, name: a.name }));

        editor = grapes.default.init({
          container: containerRef.current,
          height: EDITOR_HEIGHT,
          width: 'auto',
          storageManager: false,
          modal: { backdrop: false },
          fromElement: false,
          plugins: [preset.default],
          pluginsOpts: {
            [preset.default]: { blocks: ['text', 'link'], showBlocksOnLoad: true, modalTitleImport: 'Import HTML' }
          },
          deviceManager: {
            devices: [
              { id: 'desktop', name: 'Desktop', width: '' },
              { id: 'mobile', name: 'Mobile', width: '375px', widthMedia: '600px' }
            ]
          },
          assetManager: {
            assets: [...libraryAssets, ...iconAssets],
            upload: false,
            // Загрузка из редактора идёт в ту же Библиотеку ассетов.
            uploadFile: async (event) => {
              const files = event.dataTransfer ? event.dataTransfer.files : event.target.files;
              const form = new FormData();
              [...files].forEach((f) => form.append('files[]', f));
              const res = await fetch('/email_assets', { method: 'POST', headers: { 'X-CSRF-Token': csrfToken() }, body: form });
              const data = await res.json();
              (data.assets || []).forEach((a) => editor.AssetManager.add({ src: a.url, name: a.name }));
              if (data.errors?.length) setMessage({ error: data.errors.join('. ') });
            }
          }
        });

        registerEmailComponents(editor, publicBaseUrl);
        registerEmailBlocks(editor, publicBaseUrl);
        registerRteActions(editor);

        if (template?.design && Object.keys(template.design).length) {
          editor.loadProjectData(template.design);
          // Проект GrapesJS несёт и список ассетов на момент сохранения: без сброса
          // в выборе остаются картинки, уже удалённые из Библиотеки, — битые ссылки.
          editor.AssetManager.getAll().reset([...libraryAssets, ...iconAssets]);
        } else {
          loadHtmlIntoEditor(editor, template?.html || starterTemplate(publicBaseUrl));
        }

        // Загрузка шаблона и холста сама шлёт update — это не правка пользователя,
        // поэтому слушаем изменения только после того, как холст загрузился.
        editor.on('load', () => {
          setTimeout(() => editor.on('update', () => setDirty(true)), 500);
        });
        editorRef.current = editor;
        // Для отладки и автоматических проверок в development.
        if (process.env.NODE_ENV !== 'production') window.__emailEditor = editor;
        setReady(true);
      })
      .catch((e) => setLoadError(`Editor failed to load: ${e.message}`));

    return () => {
      cancelled = true;
      editor?.destroy();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const guard = (e) => {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, [dirty]);

  const currentHtml = useCallback(() => {
    if (mode === 'html') return htmlCode;
    return editorRef.current ? buildEmailHtml(editorRef.current) : htmlCode;
  }, [mode, htmlCode]);

  // Переключение режимов без потерь: визуальный → HTML собирает письмо из редактора,
  // HTML → визуальный загружает правленый код обратно в блоки.
  const switchMode = (next) => {
    if (next === mode || !editorRef.current) return;
    const editor = editorRef.current;

    if (mode === 'html' && htmlEdited) {
      loadHtmlIntoEditor(editor, htmlCode);
      setHtmlEdited(false);
    }
    if (next === 'html' || next === 'preview') setHtmlCode(mode === 'html' ? htmlCode : buildEmailHtml(editor));
    if (next === 'visual') setTimeout(() => editor.refresh(), 0);
    setMode(next);
  };

  const save = async () => {
    const html = currentHtml();
    if (!name.trim() || !subject.trim()) {
      setMessage({ error: 'Name and Subject are required.' });
      return;
    }

    setSaving(true);
    setMessage(null);
    try {
      // HTML, правленый руками, — источник правды: макет редактора пересоберётся из него.
      // Список ассетов в проект не пишем: он живёт в Библиотеке и подгружается заново.
      const design = mode === 'html' && htmlEdited ? null : { ...editorRef.current.getProjectData(), assets: [] };
      const res = await fetch(id ? `/email_templates/${id}` : '/email_templates', {
        method: id ? 'PATCH' : 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify({ email_template: { name, subject, preheader, html, design } })
      });
      const data = await res.json();
      if (!res.ok) throw new Error((data.errors || []).join('. ') || 'Save failed');

      if (!id) window.history.replaceState(null, '', `/email_templates/${data.id}/edit`);
      setId(data.id);
      onPersisted?.();
      setDirty(false);
      setMessage({
        ok: data.sendable ? 'Saved' : 'Saved — but the email has no Unsubscribe link, so it cannot be used in a campaign yet.'
      });
    } catch (e) {
      setMessage({ error: e.message });
    } finally {
      setSaving(false);
    }
  };

  const unsubscribeOk = ready && hasUnsubscribe(mode === 'html' ? htmlCode : currentHtml());

  return (
    <div className='bg-slate-50 min-h-screen'>
      <div className='max-w-[1600px] mx-auto px-4 py-4'>
        <div className='flex items-center justify-between gap-4 mb-3'>
          <div className='flex items-center gap-3'>
            <a href='/sms_templates?channel=email' className='text-sm text-slate-500 hover:text-slate-800'>← Messages</a>
            <h1 className='text-xl font-semibold text-slate-900'>{id ? 'Edit email' : 'New email'}</h1>
            {dirty && <span className='text-xs text-amber-600'>Unsaved changes</span>}
          </div>
          <div className='flex items-center gap-2'>
            <TestSend templateId={id} senders={senders} dirty={dirty} />
            <button type='button' onClick={save} disabled={saving || !ready} className='px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50'>
              {saving ? 'Saving…' : 'Save template'}
            </button>
          </div>
        </div>

        <div className='grid grid-cols-1 md:grid-cols-3 gap-3 bg-white border border-slate-200 rounded-lg p-3 mb-3'>
          <label className='text-xs font-medium text-slate-500'>
            Template name *
            <input className='mt-1 block w-full border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-900' value={name} onChange={(e) => { setName(e.target.value); setDirty(true); }} placeholder='e.g. Weekend reload bonus' />
          </label>
          <label className='text-xs font-medium text-slate-500'>
            Subject * <span className='font-normal text-slate-400'>— shown in the inbox</span>
            <input className='mt-1 block w-full border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-900' value={subject} maxLength={255} onChange={(e) => { setSubject(e.target.value); setDirty(true); }} placeholder='Your 100% bonus is waiting' />
          </label>
          <label className='text-xs font-medium text-slate-500'>
            Preheader <span className='font-normal text-slate-400'>— preview text after the subject</span>
            <input className='mt-1 block w-full border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-900' value={preheader} maxLength={255} onChange={(e) => { setPreheader(e.target.value); setDirty(true); }} placeholder='Only this weekend' />
          </label>
        </div>

        <div className='flex flex-wrap items-center justify-between gap-3 mb-3'>
          <div className='flex items-center gap-1 bg-slate-200/70 rounded-lg p-1'>
            <Tab active={mode === 'visual'} onClick={() => switchMode('visual')}>Visual editor</Tab>
            <Tab active={mode === 'html'} onClick={() => switchMode('html')}>HTML</Tab>
            <Tab active={mode === 'preview'} onClick={() => switchMode('preview')}>Preview</Tab>
          </div>
          {(mode === 'preview' || mode === 'visual') && (
            <div className='flex items-center gap-1 bg-slate-200/70 rounded-lg p-1'>
              <Tab active={device === 'desktop'} onClick={() => { setDevice('desktop'); editorRef.current?.setDevice('Desktop'); }}>Desktop</Tab>
              <Tab active={device === 'mobile'} onClick={() => { setDevice('mobile'); editorRef.current?.setDevice('Mobile'); }}>Mobile</Tab>
            </div>
          )}
          <div className={`text-sm px-3 py-1.5 rounded-md ${unsubscribeOk ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
            {unsubscribeOk ? '✓ Unsubscribe link present' : '✕ No Unsubscribe link — add the Unsubscribe block before sending'}
          </div>
        </div>

        {message?.ok && <div className='mb-3 text-sm text-green-800 bg-green-50 border border-green-200 rounded px-3 py-2'>{message.ok}</div>}
        {message?.error && <div className='mb-3 text-sm text-red-800 bg-red-50 border border-red-200 rounded px-3 py-2'>{message.error}</div>}
        {loadError && <div className='mb-3 text-sm text-red-800 bg-red-50 border border-red-200 rounded px-3 py-2'>{loadError}</div>}

        <div style={{ display: mode === 'visual' ? 'block' : 'none' }} className='border border-slate-200 rounded-lg overflow-hidden bg-white'>
          {!ready && !loadError && <div className='p-10 text-center text-slate-500'>Loading editor…</div>}
          <div ref={containerRef} />
        </div>

        {mode === 'html' && (
          <div>
            <p className='text-xs text-slate-500 mb-2'>
              The same email as in the visual editor. Changes here are loaded back into the blocks when you switch to the visual editor.
            </p>
            <textarea
              spellCheck={false}
              className='w-full font-mono text-xs leading-5 border border-slate-300 rounded-lg p-3 bg-slate-900 text-slate-100'
              style={{ height: EDITOR_HEIGHT }}
              value={htmlCode}
              onChange={(e) => { setHtmlCode(e.target.value); setHtmlEdited(true); setDirty(true); }}
            />
          </div>
        )}

        {mode === 'preview' && (
          <div className='bg-white border border-slate-200 rounded-lg p-4'>
            <div className='mb-3 border-b border-slate-100 pb-3'>
              <div className='text-sm font-semibold text-slate-900'>{subject || '(no subject)'}</div>
              {preheader && <div className='text-sm text-slate-500'>{preheader}</div>}
            </div>
            <EmailPreviewFrame html={htmlCode} device={device} height={700} />
          </div>
        )}
      </div>
    </div>
  );
};

const EmailEditor = (props) => {
  const [persisted, setPersisted] = useState(Boolean(props.template?.id));
  const editorType = props.template?.editor_type || props.editorType || 'grapesjs';
  if (editorType === 'beefree' && !props.beefreeEnabled) {
    return <div className='p-6 text-slate-700'>Beefree is currently disabled. This template has not been converted to the legacy editor.</div>;
  }
  const Editor = editorType === 'beefree' ? BeefreeEmailEditor : GrapesEmailEditor;
  return (
    <>
      {!persisted && props.beefreeEnabled && (
        <div className='bg-white border-b border-slate-200 px-4 py-3 text-sm flex items-center gap-4'>
          <span className='font-medium text-slate-700'>Editor for this new email:</span>
          <a data-turbolinks='false' href='/email_templates/new?editor_type=grapesjs' className={editorType === 'grapesjs' ? 'font-semibold text-blue-700' : 'text-slate-600 underline'}>Current editor</a>
          <a data-turbolinks='false' href='/email_templates/new?editor_type=beefree' className={editorType === 'beefree' ? 'font-semibold text-blue-700' : 'text-slate-600 underline'}>Beefree · Free pilot</a>
        </div>
      )}
      <Editor {...props} previewComponent={EmailPreviewFrame} testSendComponent={TestSend} onPersisted={() => setPersisted(true)} />
    </>
  );
};

export default EmailEditor;
